
import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface CalculatorInputProps {
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onKeyPress?: (e: React.KeyboardEvent) => void;
  id?: string;
  name?: string;
  className?: string;
  autoFocus?: boolean;
  step?: string;
  disabled?: boolean;
  allowNegative?: boolean;
  error?: boolean;
}

const CalculatorInput = React.forwardRef<HTMLInputElement, CalculatorInputProps>(
  ({ 
    label, 
    placeholder = "Enter a number", 
    value, 
    onChange, 
    onKeyPress,
    id,
    name,
    className,
    autoFocus = false,
    step = "0.01",
    disabled = false,
    allowNegative = false,
    error = false,
    ...props 
  }, ref) => {
    const [localValue, setLocalValue] = useState(value);

    // Sync with external value changes
    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    const validateInput = (inputValue: string) => {
      // Allow empty string
      if (inputValue === '') return true;
      
      // Allow just a minus sign if negative numbers are allowed
      if (allowNegative && inputValue === '-') return true;
      
      // Check against pattern: optional minus, digits, optional decimal separator (., comma, apostrophe, Arabic), more digits
      const pattern = allowNegative ? /^-?[0-9]*[.,''٫]?[0-9]*$/ : /^[0-9]*[.,''٫]?[0-9]*$/;
      return pattern.test(inputValue);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      
      // Validate the input
      if (validateInput(newValue)) {
        setLocalValue(newValue);
        onChange(newValue);
      }
    };

    // Check if this is meant to be an inline layout (when className includes flex-1)
    const isInlineLayout = className?.includes("flex-1");

    const getInputProps = () => {
      const baseProps = {
        ref,
        id,
        name,
        placeholder,
        value: localValue,
        onChange: handleInputChange,
        onKeyPress,
        autoFocus,
        disabled,
        "aria-label": "Calculator number input",
        ...props
      };

      // Use text type instead of number to avoid spinner buttons
      return {
        ...baseProps,
        type: "text",
        inputMode: "decimal" as const,
        step
      };
    };

    // Get error-specific styles while maintaining standard input appearance
    const getInputClassName = () => {
      const baseClasses = "text-lg";
      const errorClasses = error ? "border-red-500 text-red-600 dark:text-red-400" : "";
      return cn(baseClasses, errorClasses);
    };

    if (isInlineLayout) {
      return (
        <div className={cn("flex items-center gap-3", className)}>
          {label && (
            <Label htmlFor={id} className="text-base font-semibold whitespace-nowrap">
              {label}
            </Label>
          )}
          <Input
            {...getInputProps()}
            className={cn("flex-1", getInputClassName())}
          />
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {label && (
          <Label htmlFor={id} className="text-base font-semibold">
            {label}
          </Label>
        )}
        <Input
          {...getInputProps()}
          className={getInputClassName()}
        />
      </div>
    );
  }
);

CalculatorInput.displayName = "CalculatorInput";

export { CalculatorInput };
